import { useEffect, useRef } from "react";
import { StoreApi, UseBoundStore, create } from "zustand";

import { MaxWidthType } from "../../../@types";

import { Icon } from "@/components/atoms/Icon";
import { Screenshot, ScreenshotReplay } from "@/components/atoms/Screenshot";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";
import { ScreenshotSectionType } from "@/layouts/@types";

const TOUR_TABS_SELECTOR = '[aria-label="screenshot-tabs"]';

interface ScreenshotStore {
  activeTabIndex: number;
  /** The tour cycles tabs until the visitor picks one. */
  isAutoAdvancing: boolean;
  setActiveTabIndex: (index: number) => void;
  selectTab: (index: number) => void;
}

// Store registry to manage multiple screenshot stores
const screenshotStores: Record<
  string,
  UseBoundStore<StoreApi<ScreenshotStore>>
> = {};

/**
 * Get or create a screenshot store for a specific key
 * This allows managing multiple independent screenshot sections on the same page
 * @param key - Unique identifier for the screenshot section
 * @returns A store instance for the specified key
 */
export const getScreenshotStore = (
  key: string = "default",
): UseBoundStore<StoreApi<ScreenshotStore>> => {
  if (!screenshotStores[key]) {
    // Create a new store for this key
    const store = create<ScreenshotStore>((set) => ({
      activeTabIndex: 0,
      isAutoAdvancing: true,
      setActiveTabIndex: (index) => set({ activeTabIndex: index }),
      selectTab: (index) =>
        set({ activeTabIndex: index, isAutoAdvancing: false }),
    }));

    screenshotStores[key] = store;
  }
  return screenshotStores[key];
};

export function ScreenshotTopSection({
  layout,
}: {
  layout: ScreenshotSectionType;
  maxWidth: MaxWidthType;
}) {
  const useStore = getScreenshotStore(String(layout.title));
  const activeTabIndex = useStore((state) => state.activeTabIndex);
  const isAutoAdvancing = useStore((state) => state.isAutoAdvancing);
  const selectTab = useStore((state) => state.selectTab);

  return (
    <div className="min-w-0 flex-wrap w-full flex justify-center gap-2">
      <div role="group" aria-label="screenshot-tabs" className="flex gap-2">
        {layout.tabs.map((tab, index) => (
          <Button
            variant="outline"
            size="sm"
            aria-selected={activeTabIndex === index}
            key={tab.title}
            // Picked, its ground at rest, under the variant's hover and press.
            className={cn(
              "pl-2 pr-2 relative overflow-hidden text-foreground",
              activeTabIndex === index && "bg-accent",
            )}
            onClick={() => selectTab(index)}
          >
            <Icon ReactIcon={tab.ReactIcon} size="md" />
            {tab.title}
            {isAutoAdvancing && activeTabIndex === index ? (
              // Filled by ``ScreenshotSection`` as the scene's replay plays.
              <div
                key={activeTabIndex}
                aria-hidden
                data-tab-progress
                className="absolute left-0 right-0 bottom-0 h-0.5 bg-foreground origin-[left] transform-[scaleX(0)]"
              />
            ) : null}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function ScreenshotSection({
  layout,
}: {
  layout: ScreenshotSectionType;
  maxWidth: MaxWidthType;
}) {
  const useStore = getScreenshotStore(String(layout.title));
  const activeTabIndex = useStore((state) => state.activeTabIndex);
  const isAutoAdvancing = useStore((state) => state.isAutoAdvancing);
  const setActiveTabIndex = useStore((state) => state.setActiveTabIndex);
  const replayRef = useRef<ScreenshotReplay | null>(null);
  const tab = layout.tabs[activeTabIndex];
  const isTouring = isAutoAdvancing && layout.tabs.length > 1;

  // Fill the active tab's progress bar from the replay's clock. Each scene
  // ends by clicking the next one's sidebar item, and the tour moves on when
  // its replay does.
  useEffect(() => {
    if (!isTouring) return;
    let handle = 0;
    const tick = () => {
      handle = requestAnimationFrame(tick);
      const progress = document.querySelector<HTMLElement>(
        `${TOUR_TABS_SELECTOR} [data-tab-progress]`,
      );
      const replayer = replayRef.current?.replayer;
      if (!progress) return;
      const duration = replayer ? replayer.getMetaData().totalTime : 0;
      progress.style.transform = `scaleX(${duration ? Math.min(replayer!.getCurrentTime() / duration, 1) : 0})`;
    };
    tick();
    return () => cancelAnimationFrame(handle);
  }, [activeTabIndex, isTouring]);

  return (
    <div className="min-w-0 max-w-full pt-px">
      <Screenshot
        imageName={tab.imageName}
        alt={tab.title}
        hasBrowserFrame
        isLooping={!isTouring}
        onEnded={() =>
          setActiveTabIndex((activeTabIndex + 1) % layout.tabs.length)
        }
        replayRef={replayRef}
      />
    </div>
  );
}
